/**
 * Панель готовности (ТЗ FR-04, раздел 6.8).
 *
 * Постоянно видима: перечень блоков с отметками «заполнено / есть ошибки»,
 * общая сумма задолженности и количество кредиторов. Ошибки собираются здесь
 * и одновременно показываются рядом с полями (ТЗ п. 8.4).
 */

import { BLOCK_TITLES, type BlockId, type Readiness } from '@bd/domain';
import { money } from '@bd/doc-engine/money';

const STATE_LABEL: Record<string, string> = {
  filled: 'заполнено',
  warnings: 'есть предупреждения',
  errors: 'есть ошибки',
  empty: 'не заполнено',
};

export function ReadinessPanel({
  readiness,
  activeBlock,
  onSelectBlock,
}: {
  readiness: Readiness;
  activeBlock: BlockId;
  onSelectBlock: (block: BlockId) => void;
}) {
  return (
    <aside className="readiness">
      <div className="readiness-totals">
        <div>
          <span className="readiness-label">Общая сумма задолженности</span>
          <strong>{money(readiness.totalKopecks).digitsWithKopecks} ₽</strong>
        </div>
        <div>
          <span className="readiness-label">Кредиторов</span>
          <strong>{readiness.creditorsCount}</strong>
        </div>
      </div>

      <div className={readiness.canGenerate ? 'readiness-status ok' : 'readiness-status blocked'}>
        {readiness.canGenerate
          ? 'Проверки пройдены — можно формировать пакет'
          : `Генерация заблокирована: ${readiness.blocking.length} ошибок(ки)`}
      </div>

      <ul className="readiness-blocks">
        {(Object.keys(BLOCK_TITLES) as BlockId[]).map((block) => {
          const state = readiness.blocks[block];
          const count = readiness.issues.filter((i) => i.block === block).length;
          return (
            <li key={block}>
              <button
                type="button"
                className={`readiness-block ${state}${activeBlock === block ? ' active' : ''}`}
                onClick={() => onSelectBlock(block)}
                title={STATE_LABEL[state]}
              >
                <span className="dot" aria-hidden />
                <span className="name">{BLOCK_TITLES[block]}</span>
                {count > 0 && <span className="count">{count}</span>}
              </button>
            </li>
          );
        })}
      </ul>

      {readiness.blocking.length > 0 && (
        <section className="issues">
          <h3>Блокирующие ошибки</h3>
          <ul>
            {readiness.blocking.map((issue, i) => (
              <li key={i}>
                <button type="button" className="link-button" onClick={() => onSelectBlock(issue.block)}>
                  {BLOCK_TITLES[issue.block]}
                </button>
                : {issue.message}
              </li>
            ))}
          </ul>
        </section>
      )}

      {readiness.warnings.length > 0 && (
        <section className="issues warnings">
          <h3>Предупреждения</h3>
          <ul>
            {readiness.warnings.map((issue, i) => (
              <li key={i}>
                <button type="button" className="link-button" onClick={() => onSelectBlock(issue.block)}>
                  {BLOCK_TITLES[issue.block]}
                </button>
                : {issue.message}
              </li>
            ))}
          </ul>
        </section>
      )}
    </aside>
  );
}
